import { cn } from '@amore/ui/cn'
import { Link } from '@tanstack/react-router'
import { FlaskConical, X } from 'lucide-react'
import { useState } from 'react'
import { type Scenario, usePrototype } from '@/mock/store'
import type { Role } from '@/mock/types'

const scenarios: { value: Scenario; label: string }[] = [
  { value: 'new', label: 'Nová svatba' },
  { value: 'planning', label: 'Svatba v plánování' },
]
const roles: { value: Role; label: string }[] = [
  { value: 'owner', label: 'Správce' },
  { value: 'viewer', label: 'Pouze čtení' },
]

/** The floating "Prototyp" panel, under dialogs and sheets: switch the mocked wedding and the role. */
export function ScenarioPanel() {
  const { scenario, role, setScenario, setRole } = usePrototype()
  const [open, setOpen] = useState(false)
  return (
    <div className="fixed right-4 bottom-4 z-[15] print:hidden">
      {open ? (
        <div className="w-72 space-y-4 rounded-2xl border border-border bg-card p-4 text-sm shadow-overlay">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 font-semibold">
              <FlaskConical className="size-4 text-action" aria-hidden="true" /> Prototyp
            </span>
            <button
              type="button"
              aria-label="Skrýt panel prototypu"
              onClick={() => setOpen(false)}
              className="grid size-8 place-items-center rounded-full hover:bg-muted"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
          <Choice label="Svatba" options={scenarios} value={scenario} onChange={setScenario} />
          <Choice label="Role" options={roles} value={role} onChange={setRole} />
          <p className="text-xs text-muted-foreground">
            Data jsou smyšlená. Změny platí do obnovení stránky.
          </p>
          <div className="flex gap-3 text-xs font-semibold text-action">
            <Link to="/patterns">Vzory</Link>
            <Link to="/login">Přihlášení</Link>
            <Link to="/svatba">Web pro hosty</Link>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold shadow-overlay hover:bg-muted"
        >
          <FlaskConical className="size-4 text-action" aria-hidden="true" />
          Prototyp · {scenarios.find((item) => item.value === scenario)?.label}
          {role === 'viewer' ? ' · Pouze čtení' : ''}
        </button>
      )}
    </div>
  )
}

function Choice<V extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: V; label: string }[]
  value: V
  onChange: (value: V) => void
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-lg px-2 py-1.5 text-xs font-semibold',
              option.value === value ? 'bg-card shadow-xs' : 'text-muted-foreground',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
